import { useEffect, useState } from 'react';
import { ArrowOutUpRightSquare, Duck, Trash, User } from '@boxicons/react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import PageButtons from '../../../components/shared/PageButtons';
import RefreshRateIcon from './RefreshRateIcon';
import { FullLevel } from '../../../api/types/compounds/FullLevel';
import { LevelSubmissionSort, type LevelSubmission } from '@gdladder/shared/features';
import { getLevelSubmissions } from '../api/getLevelSubmissions';
import useRoles from '../../../hooks/api/useRoles';
import TwoPlayerButtons from './TwoPlayerButtons';
import useContextMenu from '../../../context/menu/useContextMenu';
import { PermissionFlags } from '../../admin/roles/PermissionFlags';
import useDeleteSubmissionModal from '../../../hooks/modals/useDeleteSubmissionModal';
import Level from '../types/Level';
import LevelMeta from '../types/LevelMeta';
import { createEnumParam, useQueryParam, withDefault } from 'use-query-params';
import { Device } from '@gdladder/shared/enums';
import { Heading2 } from '../../../components/headings';
import Select from '../../../components/input/select/Select';
import SegmentedButtonGroup from '../../../components/input/buttons/segmented/SegmentedButtonGroup';
import Tooltip from '../../../components/ui/Tooltip';
import SortDirectionButton from '../../../components/input/buttons/sortDirection/SortDirectionButton';
import { routes } from '../../../routes/route-definitions';
import { SortDirection } from '@gdladder/shared/enums';

const sorts: Record<LevelSubmissionSort, string> = {
    [LevelSubmissionSort.DATE_ADDED]: 'Date added',
    [LevelSubmissionSort.RATING]: 'Rating',
    [LevelSubmissionSort.ENJOYMENT]: 'Enjoyment',
    [LevelSubmissionSort.REFRESH_RATE]: 'Refresh rate',
    [LevelSubmissionSort.PROGRESS]: 'Progress',
    [LevelSubmissionSort.ATTEMPTS]: 'Attempts',
    [LevelSubmissionSort.USERNAME]: 'Username',
};

interface SubmissionProps {
    level: Level & { Meta: LevelMeta };
    submission: LevelSubmission;
}

const statusOptions = {
    all: 'All',
    completed: 'Victors',
    beating: 'Progress',
};
type StatusOptions = keyof typeof statusOptions;

function Submission({ level, submission }: SubmissionProps) {
    const { data: roles } = useRoles();
    const userRoles =
        roles && submission.User.roles.map((r) => roles.find((role) => role.ID === r)).filter((r) => r !== undefined);
    // const secondaryUserRoles = roles && submission.SecondUser.roles.map((r) => roles.find(role => role.ID === r)).filter((r) => r !== undefined);

    // Find the highest non-null role color
    const iconRole =
        userRoles
            ?.sort((a, b) => (a?.Ordering ?? 0) - (b?.Ordering ?? 0))
            .filter((r) => r?.Color !== null)
            .at(0) ?? null;
    // const secondaryIconRole = secondaryUserRoles?.sort((a, b) => (a?.Ordering ?? 0) - (b?.Ordering ?? 0)).filter((r) => r?.Color !== null).at(0) ?? null;

    const enj = submission.Enjoyment == null ? '-1' : submission.Enjoyment;
    const enjText = submission.Enjoyment == null ? '-' : submission.Enjoyment;

    const linkDestination = `/profile/${submission.User.ID}`;

    const hasWidgets = submission.Proof !== null || submission.Device === Device.MOBILE || submission.Progress < 100;

    const title: string[] = [];
    title.push(submission.User.Name);
    title.push(`Sent at: ${new Date(submission.DateAdded).toLocaleString()}`);
    title.push(`${submission.RefreshRate}fps`);
    title.push(`Progress: ${submission.Progress}%`);
    if (submission.Attempts) title.push(`Attempts: ${submission.Attempts.toLocaleString()}`);
    if (submission.Device === Device.MOBILE) title.push('Completed on mobile');

    const MAX_NAME_LENGTH = 17;
    const username = submission.User.Name;
    const shortenedName = username.length > MAX_NAME_LENGTH ? username.slice(0, MAX_NAME_LENGTH) + '...' : username;
    const secondaryName = submission.SecondUser.Name;
    const shortenedSecondaryName =
        secondaryName !== null && secondaryName.length > MAX_NAME_LENGTH
            ? secondaryName.slice(0, MAX_NAME_LENGTH) + '...'
            : secondaryName;

    const openDeleteSubmissionModal = useDeleteSubmissionModal();
    const openContext = useContextMenu([
        { text: 'View user', to: linkDestination, icon: <User size='sm' /> },
        {
            text: 'View proof',
            icon: <ArrowOutUpRightSquare size='sm' />,
            onClick: () => window.open(submission.Proof!, '_blank'),
            disabled: submission.Proof === null || submission.Proof === '',
        },
        { type: 'divider', permission: PermissionFlags.MANAGE_SUBMISSIONS },
        {
            text: 'Mod view',
            to: routes.staff.editSubmission.href(submission.ID),
            permission: PermissionFlags.MANAGE_SUBMISSIONS,
            icon: <Duck size='sm' />,
        },
        {
            text: 'Delete',
            icon: <Trash size='sm' />,
            type: 'danger',
            onClick: () => openDeleteSubmissionModal(submission.User.ID, level.ID, submission.ID, submission.User.Name),
            permission: PermissionFlags.MANAGE_SUBMISSIONS,
        },
    ]);

    return (
        <div
            onContextMenu={openContext}
            title={title.join('\n')}
            className='text-lg min-h-13 flex round:rounded-md border border-white/0 hover:border-white transition-colors shadow'
        >
            <span
                className={`flex items-center font-bold justify-center basis-14 p-2 round:rounded-s-md tier-${submission.Rating ? submission.Rating : '0'}`}
            >
                {submission.Rating ? (
                    submission.weight > 0 ? (
                        submission.Rating
                    ) : (
                        <Tooltip label={`Original rating (${submission.Rating}) has been weighted by 0`} nowrap>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='24'
                                height='24'
                                viewBox='0 0 24 24'
                                style={{ fill: 'currentColor', transform: '', msFilter: '' }}
                            >
                                <path d='M12.884 2.532c-.346-.654-1.422-.654-1.768 0l-9 17A.999.999 0 0 0 3 21h18a.998.998 0 0 0 .883-1.467L12.884 2.532zM13 18h-2v-2h2v2zm-2-4V9h2l.001 5H11z'></path>
                            </svg>
                        </Tooltip>
                    )
                ) : (
                    '-'
                )}
            </span>
            <span className={`flex items-center font-bold justify-center basis-14 p-2 text-center enj-${enj}`}>
                {enjText}
            </span>
            <Link className='p-2 flex items-center grow bg-theme-500 shrink overflow-hidden' to={linkDestination}>
                {secondaryName !== null ? (
                    <>
                        <p
                            className={iconRole ? 'font-bold' : ''}
                            style={
                                iconRole?.Color
                                    ? { color: `#${iconRole.Color.toString(16).padStart(6, '0')}` }
                                    : undefined
                            }
                        >
                            {shortenedName} {iconRole?.Icon}
                        </p>
                        {/* <p className={secondaryIconRole ? 'font-bold' : ''} style={secondaryIconRole?.Color ? { color: `#${secondaryIconRole.Color.toString(16).padStart(6, '0')}` } : undefined}>{shortenedSecondaryName} {secondaryIconRole?.Icon}</p> */}
                        <p className='text-sm text-white/70'>+ {shortenedSecondaryName}</p>
                    </>
                ) : (
                    <div className='flex items-center gap-1'>
                        {submission.User.avatar && (
                            <img
                                src={`https://cdn.gdladder.com/avatars/${submission.User.ID}/${submission.User.avatar}.png?size=32`}
                                className='rounded-full max-w-8 max-h-8 self-center inline-block'
                                alt='Profile'
                            />
                        )}
                        <p
                            className={iconRole ? 'font-bold' : ''}
                            style={
                                iconRole?.Color
                                    ? { color: `#${iconRole.Color.toString(16).padStart(6, '0')}` }
                                    : undefined
                            }
                        >
                            {shortenedName} {iconRole?.Icon}
                        </p>
                    </div>
                )}
            </Link>
            {hasWidgets && (
                <span className='flex gap-1 items-center bg-theme-500 pe-2'>
                    {submission.Device === Device.MOBILE && <i className='bx bx-mobile-alt' />}
                    {submission.Proof && (
                        <a
                            className='cursor-pointer flex items-center'
                            href={submission.Proof}
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            <i className='bx bx-link' />
                        </a>
                    )}
                    {submission.Progress < 100 && <span>{submission.Progress}%</span>}
                </span>
            )}
            <RefreshRateIcon refreshRate={submission.RefreshRate} />
        </div>
    );
}

interface SubmissionsProps {
    page: number;
    setPage: (page: number) => void;
    level: FullLevel;
    showTwoPlayerStats: boolean;
    setShowTwoPlayerStats: (show: boolean) => void;
}

export default function Submissions({
    page,
    setPage,
    level,
    showTwoPlayerStats,
    setShowTwoPlayerStats,
}: SubmissionsProps) {
    const [sorter, setSorter] = useQueryParam<LevelSubmissionSort>(
        'sort',
        withDefault(createEnumParam(Object.values(LevelSubmissionSort)), LevelSubmissionSort.DATE_ADDED),
    );
    const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASC);
    const [statusOptionsKey, setStatusOptionsKey] = useState<StatusOptions>('completed');

    const { data: submissions, status } = useQuery({
        queryKey: [
            'level',
            level.ID,
            'submissions',
            { page, status: statusOptionsKey, twoPlayer: showTwoPlayerStats, sort: sorter, sortDirection },
        ],
        queryFn: () =>
            getLevelSubmissions({
                twoPlayer: showTwoPlayerStats,
                levelID: level.ID,
                sort: sorter,
                sortDirection,
                progressFilter:
                    statusOptionsKey === 'completed'
                        ? 'victors'
                        : statusOptionsKey === 'beating'
                          ? 'incomplete'
                          : 'all',
                limit: 24,
                page,
            }),
    });

    useEffect(() => {
        if (submissions?.total === undefined || submissions.limit === undefined) return;

        if (page > 0 && submissions.data.length === 0) setPage(Math.floor(submissions?.total / submissions?.limit));
    }, [page, setPage, submissions?.data.length, submissions?.limit, submissions?.total]);

    if (status === 'error' || status === 'pending')
        return (
            <section className='mt-6'>
                <Heading2>Submissions</Heading2>
                <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2'>
                    {Array.from({ length: 24 }).map((_, i) => (
                        <SubmissionSkeleton key={i} />
                    ))}
                </div>
            </section>
        );

    return (
        <section className='mt-6'>
            <Heading2>Submissions</Heading2>
            <TwoPlayerButtons
                levelFlags={level.flags}
                levelMeta={level.Meta}
                showTwoPlayerStats={showTwoPlayerStats}
                setShowTwoPlayerStats={setShowTwoPlayerStats}
            />
            <div className='flex flex-wrap gap-2 mb-2'>
                <Select label={`Sort by: ${sorts[sorter]}`} options={sorts} onOption={setSorter} />
                <SortDirectionButton direction={sortDirection} onChange={setSortDirection} />
                <SegmentedButtonGroup
                    options={statusOptions}
                    activeKey={statusOptionsKey}
                    onSetActive={setStatusOptionsKey}
                    variant='tabs-compact'
                />
            </div>
            <div className='grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-2'>
                {submissions.data.map((s) => (
                    <Submission level={level} submission={s} key={s.User.ID} />
                ))}
                {submissions.data.length === 0 ? <p className='mb-0'>No submissions available!</p> : null}
            </div>
            <PageButtons
                onPageChange={(page) => setPage(page)}
                page={page}
                limit={submissions.limit}
                total={submissions.total}
            />
        </section>
    );
}

function SubmissionSkeleton() {
    return <div className='min-h-11.5 round:rounded-md shimmer' />;
}
